import axios from "axios";
import flatten from "lodash-es/flatten";
import uniq from "lodash-es/uniq";
import zipObject from "lodash-es/zipObject";

import { defineStore } from "pinia";
import { markRaw } from "vue";

import { MiaPlaza } from "@/Reinforced.Typings";

import AjaxHelpers = MiaPlaza.Control.Vue.Stores.Shared.AjaxHelpers;
import QueueStore from "./QueueStore";

export type ConversionProvider<S, T> = (keys: S[]) => Promise<T[]>;

// This is essentially a dictionary mapping "data" (say member ids) to their conversions (say
// detailed member data for display.) This resembles a REST interface but can be a bit more flexible.
// (Note also that implementing REST in ASP.NET was too rigid at the time so it did not seem worth the effort.)
export function createConversionStore<S, T>(id: string, conversionProvider: ConversionProvider<S, T>, initialData: { [key: string]: T }, reset: boolean) {
	let created = false;

	const store = defineStore(id, {
		state: () => {
			created = true;
			return {
				conversionProvider: conversionProvider,
				data: initialData,
				generation: 1,
				internal: markRaw({
					queue: new QueueStore<S[]>(),
				}),
			};
		},
		getters: {
			items: (state) => {
				return state.data;
			},
			isResetting: (state) => {
				if (Object.keys(state.data).length === 0) {
					return state.generation;
				}

				return 0;
			},
		},
		actions: {
			async reset(conversionProvider?: string | ConversionProvider<S, T>): Promise<void> {
				this.generation++;
				this.internal.queue.markInvalid();
				if (conversionProvider != null) {
					if (typeof (conversionProvider) === "string") {
						conversionProvider = makeConversionProvider<S, T>(conversionProvider);
					}
					this.conversionProvider = conversionProvider;
				}
				this.data = {};
			},
			// Return a promise that resolves once all keys have been loaded.
			async load(keys: S[]): Promise<void> {
				if (keys == null || keys.length === 0) {
					return;
				}
				this.internal.queue.enqueue(keys);
				await this.internal.queue.dequeue(this.doLoad);
			},
			async doLoad(keys: S[][], isValid: () => boolean): Promise<void> {
				const flatKeys = uniq(flatten(keys))
					.filter((key) => this.data[key as unknown as string] === undefined);

				if (flatKeys.length === 0) {
					return;
				}

				const response = await this.conversionProvider(flatKeys);

				if (!isValid()) {
					return;
				}

				this.insert(Object.entries(zipObject(flatKeys as unknown[] as string[], response)));
			},
			async invalidate(key: S): Promise<void> {
				// We do not trigger a reload here. Reloading here is wrong as we might have to wait until some backend action has
				// completed. Also, reloads should be debounced and therefore not be triggered down here.
				this.doInvalidate(key);
				await Promise.resolve();
			},
			doInvalidate(key: S): void {
				delete this.data[key];
			},
			insert(items: Array<[string, T]>): void {
				for (const [key, value] of items) {
					this.data[key.toString()] = value;
				}
			},
		},
	})();

	if (!created && reset) {
		store.reset(conversionProvider);
	}

	return store;
}

export type IConversionStoreDefinition<S, T> = ReturnType<typeof createConversionStore<S, T>>;
export type IConversionStore<S, T> = ReturnType<IConversionStoreDefinition<S, T>>;

export function useConversionStore<S, T>(id: string): IConversionStore<S, T> {
	return defineStore(id, {
		state: () => { throw Error(`store ${id} must be created with defineConversionStore() before it can be used`); },
	})();
}

export function makeConversionProvider<S, T>(converterUrl: string): ConversionProvider<S, T> {
	return async (keys: S[]) => {
		const response = await axios.get<T[]>(converterUrl
			.replace(AjaxHelpers.PLACEHOLDER_CONVERT_MANY, encodeURIComponent(JSON.stringify(keys))));
		return response.data;
	};
}
